import React, { useContext, useEffect, useRef } from "react";
import {
    ExclamationTriangleIcon,
    ExclamationCircleIcon,
    CircleCheckStrokeIcon,
    ClockIcon as PanwdsClockIcon,
    InfoIcon as PanwdsInfoIcon,
} from "@panwds/icons";
import isEmpty from 'lodash';
import DegradedIcon from '../assets/icons/DegradedIcon.svg';
import AvailableIcon from '../assets/icons/AvailableIcon.svg';
import InfoIcon from '../assets/icons/InfoIcon.svg';
import ClockIcon from '../assets/icons/ClockIcon.svg';
import InvestigatingIcon from '../assets/icons/InvestigatingIcon.svg';
import DisruptionIcon from '../assets/icons/DisruptionIcon.svg';

export const TIME_RANGE_KEYS_ALL = ['24h', '7d', '30d', '90d', '1y'];
export const TIME_RANGE_KEYS = ['24h', '7d', '30d', '90d', '1y'];
export const TIME_RANGE_KEYS_LONG_RANGE = ['30d', '90d'];
export const PAST_TIME_RANGE_OPTION_MAP = {
    // '1h': 'Past 1 Hour',
    // '3h': 'Past 3 Hours',
    '24h': 'Past 24 Hours',
    '7d': 'Past 7 days',
    '30d': 'Past 30 days',
    '90d': 'Past 90 days',
    '1y': 'Past 1 Year',
};
export const TIME_RANGE_OPTION_MAP = {
    // '1h': '1 Hour',
    // '3h': '3 Hours',
    '24h': '24 Hours',
    '7d': '7 days',
    '30d': '30 days',
    '90d': '90 days',
    '1y': '1 Year',
};


export const PATH_TYPE_OPTION_MAP = {
    DirectInternet: 'Direct Internet',
    VPN: 'Internet VPN',
    PrivateWAN: 'Direct Private WAN',
    PrivateVPN: 'Private WAN VPN',
    ServiceLink: 'Standard VPN',
};

// available API values for "filter.path_type"
export const PATH_TYPES = ['DirectInternet', 'VPN', 'PrivateWAN', 'PrivateVPN', 'ServiceLink'];

export const getPathTypeOptions = ({ omit = [], keyList = [], labelAttr = 'children' } = {}) => {
    return getSelectOptions({
        omit,
        labelAttr,
        keyList: !isEmpty(keyList) ? keyList : PATH_TYPES,
        selectOptionMap: PATH_TYPE_OPTION_MAP,
    });
};

// build a list of select options (ex: [{ children: 'abcLabel', value: 'abcValue' }, ...] using an object map)
export const getSelectOptions = ({ omit = [], labelAttr = 'children', keyList = [], selectOptionMap = {} } = {}) => {
    const selectOptions = [];

    // if no ordered keyList is passed in, derive from object
    if (isEmpty(keyList)) {
        keyList.push(...Object.keys(selectOptionMap));
    }

    keyList.forEach(itemKey => {
        // check option to skip
        if (Array.isArray(omit) && omit.includes(itemKey) && !!isEmpty(selectOptions.find(s => s.value === itemKey))) {
            return;
        }

        // check option to skip
        if (!selectOptions.find(s => s.value === itemKey)) {
            selectOptions.push({
                [labelAttr]: selectOptionMap[itemKey],
                value: itemKey,
            });
        }
    });

    return selectOptions;
};

export const getTimeRangeSelectOptions = ({
    omit = [],
    keyList = [],
    labelAttr = 'children',
    showPastLabel = true,
} = {}) => {
    let tmpKeyList = [];

    if (!isEmpty(keyList)) {
        tmpKeyList = keyList;
    } else {
        tmpKeyList = TIME_RANGE_KEYS_ALL;
    }

    return getSelectOptions({
        omit,
        labelAttr,
        keyList: tmpKeyList,
        selectOptionMap: showPastLabel ? PAST_TIME_RANGE_OPTION_MAP : TIME_RANGE_OPTION_MAP,
    });
};

export const usePrevious = value => {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
}

/**
 * Simple context to be provided to all components.
 * Based on primitive string value.
 */

export const PathStateContext = React.createContext(undefined);

PathStateContext.displayName = "PathStateContext";

export const PathStateProvider = PathStateContext.Provider;
export const PathProviderContext = PathStateContext.Consumer;
export function usePathStateContext() {
    return useContext(PathStateContext);
}
export default PathStateProvider;

/**
 * Intakes a path and replaces the / with _ to denote separate pendo categories.
 * Snake case is replaced with - to remain in the same path category
 * * "/monitor/sase-branch-sites/details/bs_details" => "monitor_sase-branch-sites_details_bs-details"
 */
export const pathFormatter = pathname => {
    if (typeof pathname !== "string") {
        return "";
    }
    let formatted = "";
    for (let i = 1; i < pathname.length; i++) {
        if (pathname[i] === "/") {
            formatted += "_";
        } else if (pathname[i] === "_") {
            formatted += "-";
        } else {
            formatted += pathname[i];
        }
    }
    return formatted;
}

/**
 * Distinguishes between click handlers in the case of different views and in the case of
 * different widgets. It lists the action of the handler.
 *
 * Replaces any underscores in id with tacs
 */
export const datametricsFormatter = (
    formattedPath,
    id,
    actionName
) => `${formattedPath}_${id?.replace(/_/g, "-")}_${actionName}`

export const parseUrlSearchString = searchString => {
    const result = {};
    const params = new URLSearchParams(searchString);

    params.keys().forEach(key => {
        const val = params.get(key);

        result[key] = val;
    });

    return result;
};

export const getUrlQueryParams = () => {
    return parseUrlSearchString(window.location.search);
};

export const getUrlQueryParam = (param, searchString) => {
    const params = new URLSearchParams(searchString || window.location.search);

    return params.get(param);
};

export const MAP_STATUS_COLORS = {
    available: '#33CCB8',
    degraded: '#FFD745',
    disruption: '#E14E4E',
};

export const MAP_STATUS_RGB_COLORS = {
    available: [51, 204, 184], // #33CCB8
    degraded: [255, 215, 69], // #FFD745
    disruption: [225, 78, 78], // #E14E4E
};

export const MAP_STATUS_LABELS = {
    available: 'Operational',
    degraded: 'Degraded',
    disruption: 'Disruption',
};

export const getTimeZoneAbbreviation = (timeZone, timeZoneName = 'short') => {
    const formatter = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName });
    const formatted = formatter.formatToParts(new Date());
    const timeZoneNamePart = formatted.find(part => part.type === 'timeZoneName');

    return timeZoneNamePart?.value || timeZone;
};

export const getLocalBrowserTimeZone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const PLANE_TYPE = {
    data: 'Data',
    control: 'Control',
    all: 'All'
};

export const MAP_PLANE_TYPES = {
    data_plane: {
        iconTooltip: 'Compute Location',
        locationLabel: 'Compute Locations',
        shortLabel: 'Data',
        PAshortLabel: 'Compute',
        btnLabel: 'Compute Location',
    },
    control_plane: {
        iconTooltip: 'Control Plane',
        locationLabel: 'Control Plane Locations',
        shortLabel: 'Control Plane',
        btnLabel: 'Control Plane',
    },
    service_plane: {
        iconTooltip: 'Prisma Access Service Plane',
        locationLabel: 'Prisma Access Service Plane Locations',
        shortLabel: 'Service Plane',
    },
    // all: {
    //     iconTooltip: 'Prisma Access Location',
    //     locationLabel: 'Prisma Access Locations',
    //     shortLabel: 'Prisma Access',
    //     PAshortLabel: 'Prisma Access',
    //     btnLabel: 'Prisma Access Location',
    // },
    all: {
        iconTooltip: '  Location',
        locationLabel: '  Locations',
        shortLabel: 'All',
        PAshortLabel: 'Prisma Access',
        btnLabel: ' Location',
    },
};

export const iconMapping = {
    monitoring: <img src={InfoIcon} alt='' width='15' height='15' />,
    Monitoring: <img src={InfoIcon} alt='' width='15' height='15' />,
    investigating: <img src={InvestigatingIcon} alt='' width='15' height='15' />,
    Investigating: <img src={InvestigatingIcon} alt='' width='15' height='15' />,
    Degraded: <img src={DegradedIcon} alt='' width='15' height='15' />,
    degraded: <img src={DegradedIcon} alt='' width='15' height='15' />,
    disruption: <img src={DisruptionIcon} alt='' width='15' height='15' />,
    Disruption: <img src={DisruptionIcon} alt='' width='15' height='15' />,
    resolved: <img src={AvailableIcon} alt='' width='15' height='15' />,
    Open: <img src={InfoIcon} alt='' width='15' height='15' />,
    'In-Progress': <img src={DegradedIcon} alt='' width='15' height='15' />,
    InProgress: <img src={DegradedIcon} alt='' width='15' height='15' />,
    Ongoing: <img src={DegradedIcon} alt='' width='15' height='15' />,
    Scheduled: <img src={ClockIcon} alt='' width='15' height='15' />,
    'In Progress': <img src={DegradedIcon} alt='' width='15' height='15' />,
    Resolved: <img src={AvailableIcon} alt='' width='15' height='15' />,
    Closed: <img src={AvailableIcon} alt='' width='15' height='15' />,
    Completed: <img src={AvailableIcon} alt='' width='15' height='15' />,
    clock: <img src={ClockIcon} alt='' width='15' height='15' />
};

export const getStatusSVG = (status = 'resolved') => {
    let statusCleaned = status?.toLowerCase?.() || 'resolved';

    statusCleaned = statusCleaned.replace(/[- ]/g, '');

    if (['monitoring', 'open'].includes(statusCleaned)) {
        return InfoIcon;
    }

    if ([
        'investigating',
        'degraded',
        'inprogress',
        'ongoing',
    ].includes(statusCleaned)) {
        return DegradedIcon;
    }

    if (['scheduled', 'clock'].includes(statusCleaned)) {
        return ClockIcon;
    }

    if (['resolved', 'closed', 'completed'].includes(statusCleaned)) {
        return AvailableIcon;
    }

    return AvailableIcon;
};

export const getStatusPanwdsIcon = (status = 'resolved') => {
    let statusCleaned = status?.toLowerCase?.() || 'resolved';

    // remove spaces, dashes, etc.
    statusCleaned = statusCleaned.replace(/[- ]/g, '');

    if (['monitoring', 'open'].includes(statusCleaned)) {
        return ExclamationCircleIcon;
    }

    if ([
        'investigating',
        'degraded',
        'inprogress',
        'ongoing',
    ].includes(statusCleaned)) {
        return ExclamationTriangleIcon;
    }

    if (['scheduled', 'clock'].includes(statusCleaned)) {
        return PanwdsClockIcon;
    }

    if (['resolved', 'closed', 'completed'].includes(statusCleaned)) {
        return CircleCheckStrokeIcon;
    }

    return PanwdsInfoIcon;
};

export const ALL_REGIONS = [
    {
        id: 'North America',
        value: 'North America',
    },
    {
        id: 'South America',
        value: 'South America',
    },
    {
        id: 'Europe',
        value: 'Europe',
    },
    {
        id: 'Middle East',
        value: 'Middle East',
    },
    {
        id: 'Asia Pacific',
        value: 'Asia Pacific',
    },
    {
        id: 'Africa',
        value: 'Africa',
    }
];
